import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Upload, FileText, LogOut, CheckCircle, Clock, AlertCircle, Zap, Users, Trash2, Calendar, ChevronDown, ChevronUp, Search, Archive, Mail, Link2, X } from 'lucide-react';
import ConsentModal from '../components/ConsentModal';
import PendingBookings from '../components/PendingBookings';
import PendingReferrerApprovals from '../components/PendingReferrerApprovals';
import ReferralPipeline from '../components/ReferralPipeline';
import API_BASE_URL from '../config/api';
import { useClinic } from '../contexts/ClinicContext';
import { getAvailableTemplates } from '../config';

function DashboardPage() {
  const [referrals, setReferrals] = useState([]);
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [deleting, setDeleting] = useState({});
  const [archiving, setArchiving] = useState({});
  const [resending, setResending] = useState({});
  const [resendingForm, setResendingForm] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showBookings, setShowBookings] = useState(true);
  const [showPipeline, setShowPipeline] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [linking, setLinking] = useState({});
  const { user, logout } = useAuth();
  const { activeClinicId } = useClinic();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Diary "link to existing patient" flow: when the diary routes here for an
  // unlinked Gensolve appointment, these params carry the appointment's
  // CLIENT_ID and CLIENT_NAME. We pre-fill the search box and surface a
  // "Link" action on each referral row.
  const linkGensolveId = searchParams.get('linkGensolveId');
  const linkGensolveName = searchParams.get('linkGensolveName');
  const incomingSearch = searchParams.get('search');

  useEffect(() => {
    if (incomingSearch && !searchQuery) setSearchQuery(incomingSearch);
    // We only want to apply the URL-provided search once on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingSearch]);

  const exitLinkMode = () => {
    setSearchParams({});
  };

  const handleLinkGensolve = async (patientId, patientName) => {
    if (!linkGensolveId) return;
    if (!window.confirm(`Link "${patientName}" to Gensolve client "${linkGensolveName || linkGensolveId}"?\n\nFuture diary appointments for this patient will resolve automatically.`)) return;
    setLinking(prev => ({ ...prev, [patientId]: true }));
    try {
      await axios.post(`${API_BASE_URL}/patients/${patientId}/link-gensolve`, {
        gensolveClientId: linkGensolveId
      });
      navigate('/diary');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to link patient';
      alert('Could not link: ' + msg);
      setLinking(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';

  useEffect(() => {
    fetchData();
  }, [activeClinicId]);

  const fetchReferrals = async () => {
    const res = await axios.get(`${API_BASE_URL}/referrals${clinicQuery}`);
    const data = res.data.data || res.data;
    setReferrals(data);
    return data;
  };

  const fetchLetters = async () => {
    const res = await axios.get(`${API_BASE_URL}/letters${clinicQuery}`);
    const data = res.data.data || res.data;
    setLetters(data);
    return data;
  };

  const fetchData = async () => {
    try {
      await Promise.all([fetchReferrals(), fetchLetters()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAll = async (referralId, patientId) => {
    console.log('Generating all documents for referral:', referralId, 'patient:', patientId);
    setGenerating(prev => ({ ...prev, [referralId]: true }));
    
    try {
      const response = await axios.post(`${API_BASE_URL}/letters/generate-all/${patientId}`, {
        referralId: referralId,
        includeMRI: true
      });
      console.log('All documents generated:', response.data);

      alert('Consultation letter and radiology referral generated successfully! View them in the Documents page.');
      // Only refetch letters since referrals haven't changed
      await fetchLetters();
    } catch (error) {
      console.error('Error generating documents:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to generate documents';
      alert('Error: ' + errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, [referralId]: false }));
    }
  };

  const handleDeleteReferral = async (referralId, patientName) => {
    if (!window.confirm(`Are you sure you want to delete the referral for ${patientName}? This will also delete any associated forms and letters.`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [referralId]: true }));
    
    try {
      await axios.delete(`${API_BASE_URL}/referrals/${referralId}`);
      alert('Referral deleted successfully');
      // Only refetch referrals since letters haven't changed
      await fetchReferrals();
    } catch (error) {
      console.error('Error deleting referral:', error);
      alert('Failed to delete referral: ' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(prev => ({ ...prev, [referralId]: false }));
    }
  };

  const handleResendSlots = async (patientId, patientName) => {
    if (!window.confirm(`Reset booking status and resend appointment slots to ${patientName}?`)) {
      return;
    }

    setResending(prev => ({ ...prev, [patientId]: true }));

    try {
      const response = await axios.post(`${API_BASE_URL}/booking/repropose/${patientId}`);
      alert(`Appointment slots resent to ${response.data.patientEmail || patientName}!`);
    } catch (error) {
      console.error('Error resending slots:', error);
      alert('Failed to resend slots: ' + (error.response?.data?.error || error.message));
    } finally {
      setResending(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const handleResendForm = async (patientId, patientName, currentEmail) => {
    const email = window.prompt(
      `Send the patient form email to ${patientName}.\n\nConfirm or update the email address:`,
      currentEmail || ''
    );

    if (email === null) return; // cancelled
    if (!email.trim()) {
      alert('Email address is required.');
      return;
    }

    setResendingForm(prev => ({ ...prev, [patientId]: true }));

    try {
      const response = await axios.post(`${API_BASE_URL}/patients/${patientId}/send-form-email`, { email: email.trim() });
      alert(`Form email sent to ${response.data.email || email.trim()}!`);
    } catch (error) {
      console.error('Error resending form email:', error);
      alert('Failed to resend form email: ' + (error.response?.data?.error || error.message));
    } finally {
      setResendingForm(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const handleArchivePatient = async (patientId, patientName, isArchived) => {
    const action = isArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`${isArchived ? 'Unarchive' : 'Archive'} ${patientName}?`)) return;
    setArchiving(prev => ({ ...prev, [patientId]: true }));
    try {
      await axios.patch(`${API_BASE_URL}/patients/${patientId}/archive`);
      fetchReferrals();
    } catch (error) {
      console.error(`Error ${action} patient:`, error);
      alert(`Failed to ${action} patient`);
    } finally {
      setArchiving(prev => ({ ...prev, [patientId]: false }));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981' }} />;
      case 'pending':
        return <Clock style={{ width: '20px', height: '20px', color: '#f59e0b' }} />;
      default:
        return <AlertCircle style={{ width: '20px', height: '20px', color: '#9ca3af' }} />;
    }
  };

  // Split referrals into active vs completed vs archived
  const COMPLETED_STATUSES = ['booked', 'confirmed', 'cancelled'];
  const archivedReferrals = referrals.filter(r => r.patient?.archivedAt);
  const nonArchivedReferrals = referrals.filter(r => !r.patient?.archivedAt);
  const completedReferrals = nonArchivedReferrals.filter(r =>
    COMPLETED_STATUSES.includes(r.patient?.appointmentStatus) ||
    r.patient?.letterSent
  );
  const activeReferrals = nonArchivedReferrals.filter(r =>
    !COMPLETED_STATUSES.includes(r.patient?.appointmentStatus) &&
    !r.patient?.letterSent
  );

  // Select list based on active tab, then apply search filter
  const tabReferrals = activeTab === 'archived' ? archivedReferrals : activeTab === 'active' ? activeReferrals : completedReferrals;
  const filteredReferrals = searchQuery
    ? tabReferrals.filter(r => {
        const q = searchQuery.toLowerCase();
        return (r.patient?.name || '').toLowerCase().includes(q) ||
               (r.patient?.email || '').toLowerCase().includes(q) ||
               (r.patient?.nhi || '').toLowerCase().includes(q);
      })
    : tabReferrals;

  // Group filtered referrals by injury type
  const referralsByType = filteredReferrals.reduce((acc, referral) => {
    const type = referral.injuryType || 'UNKNOWN';
    if (!acc[type]) acc[type] = [];
    acc[type].push(referral);
    return acc;
  }, {});

  const injuryTypeLabels = Object.fromEntries([
    ...getAvailableTemplates().map(t => [t.id, t.name]),
    ['UNKNOWN', 'Other']
  ]);

  // Get referrals ready for letter generation (form completed, no letters generated yet, not archived)
  // Uses per-referral formCompleted flag so a new referral for the same patient doesn't hide completed ones
  const getSurname = (p) => {
    if (p?.lastName) return p.lastName;
    const parts = (p?.name || '').trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : (parts[0] || '');
  };
  const readyForLetters = nonArchivedReferrals
    .filter(r =>
      (r.formCompleted || r.patient?.formCompleted) && !letters.some(l => l.referralId === r.id)
    )
    .sort((a, b) => getSurname(a.patient).localeCompare(getSurname(b.patient), undefined, { sensitivity: 'base' }));

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Main Content */}
      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>Dashboard</h1>

        {/* Diary link-mode banner: shown when the user clicked an unlinked diary appointment */}
        {linkGensolveId && (
          <div style={{
            backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
            padding: '12px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Link2 size={18} color="#92400e" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: 14, color: '#92400e', minWidth: 0 }}>
                <strong>Link diary appointment</strong>
                {' '}— click <em>Link</em> next to the matching patient below to associate them with Gensolve client
                {' '}<strong>{linkGensolveName || `#${linkGensolveId}`}</strong>.
              </div>
            </div>
            <button
              onClick={exitLinkMode}
              title="Cancel linking"
              style={{ background: 'transparent', border: 'none', color: '#92400e', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Pending Referrer Approvals - shows at top when there are pending approvals */}
        <PendingReferrerApprovals />

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <button
            onClick={() => setShowBookings(!showBookings)}
            style={{
              backgroundColor: '#e8941c',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d97706'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e8941c'}
          >
            <Calendar style={{ width: '24px', height: '24px', marginRight: '12px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Manage Bookings</span>
          </button>

          <button
            onClick={() => navigate('/upload')}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            <Upload style={{ width: '24px', height: '24px', marginRight: '12px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Upload Referral</span>
          </button>
        </div>

        {/* Referral Pipeline */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          marginBottom: '32px',
          border: '2px solid #0d9488',
          overflow: 'hidden'
        }}>
          <div
            style={{
              padding: '16px 24px',
              background: '#0d9488',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setShowPipeline(!showPipeline)}
          >
            <h2 style={{ fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', margin: 0 }}>
              <Users style={{ width: '24px', height: '24px', marginRight: '8px' }} />
              Referral Pipeline
            </h2>
            {showPipeline ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>
          {showPipeline && <ReferralPipeline />}
        </div>

        {/* Appointment Bookings Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          marginBottom: '32px',
          border: '2px solid #e8941c',
          overflow: 'hidden'
        }}>
          <div
            style={{
              padding: '16px 24px',
              background: '#e8941c',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setShowBookings(!showBookings)}
          >
            <h2 style={{ fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', margin: 0 }}>
              <Calendar style={{ width: '24px', height: '24px', marginRight: '8px' }} />
              Appointment Bookings
            </h2>
            {showBookings ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>
          {showBookings && <PendingBookings />}
        </div>

        {/* Statistics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Total Referrals</p>
                <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{referrals.length}</p>
              </div>
              <Upload style={{ width: '48px', height: '48px', color: '#3b82f6', opacity: 0.2 }} />
            </div>
          </div>
          
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Generated Letters</p>
                <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{letters.length}</p>
              </div>
              <FileText style={{ width: '48px', height: '48px', color: '#10b981', opacity: 0.2 }} />
            </div>
          </div>
          
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Pending Review</p>
                <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                  {letters.filter(l => l.status === 'pending_review').length}
                </p>
              </div>
              <Clock style={{ width: '48px', height: '48px', color: '#f59e0b', opacity: 0.2 }} />
            </div>
          </div>

          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Ready for Letters</p>
                <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{readyForLetters.length}</p>
              </div>
              <Zap style={{ width: '48px', height: '48px', color: '#a855f7', opacity: 0.2 }} />
            </div>
          </div>
        </div>

        {/* Ready for Letters Section */}
        {readyForLetters.length > 0 && (
          <div style={{
            background: 'linear-gradient(to right, #faf5ff, #eff6ff)',
            border: '2px solid #e9d5ff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            marginBottom: '32px'
          }}>
            <div style={{
              padding: '16px 24px',
              background: '#9333ea',
              color: 'white',
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', margin: 0 }}>
                <Zap style={{ width: '24px', height: '24px', marginRight: '8px' }} />
                Ready for Letter Generation
              </h2>
              <p style={{ fontSize: '14px', color: '#e9d5ff', marginTop: '4px', marginBottom: 0 }}>
                These referrals have completed forms and are ready for letter generation
              </p>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {readyForLetters.map((referral) => (
                  <div key={referral.id} style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    padding: '16px',
                    border: '2px solid #e9d5ff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <h3 style={{ fontWeight: '600', color: '#111827', marginBottom: '8px', marginTop: 0 }}>
                        {referral.patient?.name}
                      </h3>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#f3e8ff',
                        color: '#7c3aed',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {injuryTypeLabels[referral.injuryType] || referral.injuryType}
                      </span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px', marginTop: 0 }}>
                      Form completed {referral.patient?.formCompletedAt ? new Date(referral.patient.formCompletedAt).toLocaleDateString('en-NZ') : 'recently'}
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleGenerateAll(referral.id, referral.patient?.id)}
                        disabled={generating[referral.id]}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          backgroundColor: generating[referral.id] ? '#9ca3af' : '#9333ea',
                          color: 'white',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: generating[referral.id] ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        <Zap style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                        {generating[referral.id] ? 'Generating...' : 'Generate All Docs'}
                      </button>
                      <button
                        onClick={() => navigate(`/patient-file/${referral.patient?.id}`)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="View patient file"
                      >
                        <FileText style={{ width: '16px', height: '16px' }} />
                      </button>
                      <button
                        onClick={() => handleDeleteReferral(referral.id, referral.patient?.name)}
                        disabled={deleting[referral.id]}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: deleting[referral.id] ? '#9ca3af' : '#fee2e2',
                          color: deleting[referral.id] ? 'white' : '#dc2626',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: deleting[referral.id] ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Delete referral"
                      >
                        <Trash2 style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tabs + Search */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setActiveTab('active')}
              style={{
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === 'active' ? '#2563eb' : 'white',
                color: activeTab === 'active' ? 'white' : '#374151',
                transition: 'background-color 0.15s, color 0.15s'
              }}
            >
              Active ({activeReferrals.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              style={{
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderLeft: '1px solid #e5e7eb',
                cursor: 'pointer',
                backgroundColor: activeTab === 'completed' ? '#2563eb' : 'white',
                color: activeTab === 'completed' ? 'white' : '#374151',
                transition: 'background-color 0.15s, color 0.15s'
              }}
            >
              Completed ({completedReferrals.length})
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              style={{
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderLeft: '1px solid #e5e7eb',
                cursor: 'pointer',
                backgroundColor: activeTab === 'archived' ? '#6b7280' : 'white',
                color: activeTab === 'archived' ? 'white' : '#374151',
                transition: 'background-color 0.15s, color 0.15s'
              }}
            >
              Archived ({archivedReferrals.length})
            </button>
          </div>
          <div style={{ position: 'relative', flex: '1', minWidth: '220px', maxWidth: '400px' }}>
            <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#9ca3af' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or NHI..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>
        </div>

        {/* Referrals by Type */}
        {Object.keys(referralsByType).length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b7280',
            marginBottom: '24px'
          }}>
            {searchQuery
              ? `No ${activeTab} referrals found matching "${searchQuery}"`
              : `No ${activeTab} referrals`}
          </div>
        ) : null}
        {Object.entries(referralsByType).map(([type, typeReferrals]) => (
          <div key={type} style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
                {injuryTypeLabels[type] || type} Referrals ({typeReferrals.length})
              </h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Patient</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Date</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Form Status</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Letter</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {typeReferrals.map((referral, index) => {
                    const hasLetter = letters.some(l => l.referralId === referral.id);
                    return (
                      <tr key={referral.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{referral.patient?.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{referral.patient?.email}</div>
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280' }}>
                          {new Date(referral.uploadedAt).toLocaleDateString('en-NZ')}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          {referral.patient?.formCompleted ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: '#dcfce7',
                              color: '#166534'
                            }}>
                              <CheckCircle style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                              Completed
                            </span>
                          ) : (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: '#fef3c7',
                              color: '#92400e'
                            }}>
                              <Clock style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                              Pending
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          {hasLetter ? (
                            <button
                              onClick={() => navigate(`/patient-file/${referral.patient?.id}`)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 10px',
                                borderRadius: '9999px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              title="Click to view patient file"
                            >
                              <CheckCircle style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                              Generated
                            </button>
                          ) : (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 10px',
                              borderRadius: '9999px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280'
                            }}>
                              Pending
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {linkGensolveId && referral.patient?.id && (
                              <button
                                onClick={() => handleLinkGensolve(referral.patient.id, referral.patient.name)}
                                disabled={linking[referral.patient.id]}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: linking[referral.patient.id] ? '#9ca3af' : '#fde68a',
                                  color: linking[referral.patient.id] ? 'white' : '#92400e',
                                  borderRadius: '6px',
                                  border: '1px solid #f59e0b',
                                  cursor: linking[referral.patient.id] ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '13px',
                                  fontWeight: '600'
                                }}
                                title={`Link this patient to Gensolve client "${linkGensolveName || linkGensolveId}"`}
                              >
                                <Link2 style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                {linking[referral.patient.id] ? 'Linking...' : 'Link'}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/patient-file/${referral.patient?.id}`)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '13px',
                                fontWeight: '500'
                              }}
                              title="View patient file with forms and documents"
                            >
                              <FileText style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                              View File
                            </button>
                            {referral.patient?.email && referral.patient?.formUrl && !referral.patient?.formCompleted && (
                              <button
                                onClick={() => handleResendForm(referral.patient?.id, referral.patient?.name, referral.patient?.email)}
                                disabled={resendingForm[referral.patient?.id]}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: resendingForm[referral.patient?.id] ? '#9ca3af' : '#dcfce7',
                                  color: resendingForm[referral.patient?.id] ? 'white' : '#166534',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: resendingForm[referral.patient?.id] ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                                title="Resend patient form email"
                              >
                                <Mail style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                {resendingForm[referral.patient?.id] ? 'Sending...' : 'Resend Form'}
                              </button>
                            )}
                            {referral.patient?.formCompleted && (
                              <button
                                onClick={() => handleResendSlots(referral.patient?.id, referral.patient?.name)}
                                disabled={resending[referral.patient?.id]}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: resending[referral.patient?.id] ? '#9ca3af' : '#fef3c7',
                                  color: resending[referral.patient?.id] ? 'white' : '#92400e',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: resending[referral.patient?.id] ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                                title="Reset booking and resend appointment slot options"
                              >
                                <Calendar style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                {resending[referral.patient?.id] ? 'Sending...' : 'Resend Slots'}
                              </button>
                            )}
                            <button
                              onClick={() => handleArchivePatient(referral.patient?.id, referral.patient?.name, referral.patient?.archivedAt)}
                              disabled={archiving[referral.patient?.id]}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: archiving[referral.patient?.id] ? '#9ca3af' : referral.patient?.archivedAt ? '#dbeafe' : '#f3f4f6',
                                color: archiving[referral.patient?.id] ? 'white' : referral.patient?.archivedAt ? '#1e40af' : '#374151',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: archiving[referral.patient?.id] ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '13px',
                                fontWeight: '500'
                              }}
                              title={referral.patient?.archivedAt ? 'Unarchive patient' : 'Archive patient'}
                            >
                              <Archive style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                              {archiving[referral.patient?.id] ? '...' : referral.patient?.archivedAt ? 'Unarchive' : 'Archive'}
                            </button>
                            <button
                              onClick={() => handleDeleteReferral(referral.id, referral.patient?.name)}
                              disabled={deleting[referral.id]}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: deleting[referral.id] ? '#9ca3af' : '#fee2e2',
                                color: deleting[referral.id] ? 'white' : '#dc2626',
                                borderRadius: '6px',
                                border: 'none',
                                cursor: deleting[referral.id] ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '13px',
                                fontWeight: '500'
                              }}
                            >
                              <Trash2 style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                              {deleting[referral.id] ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Recent Documents */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Recent Documents</h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            {letters.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
                No documents generated yet. Complete forms and click "Letter" or "Radiology Referral" above.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Patient</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Generated</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {letters.slice(0, 10).map((letter, index) => (
                    <tr key={letter.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                      <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {letter.patient?.name || 'Unknown'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280' }}>
                        {new Date(letter.generatedAt).toLocaleString('en-NZ')}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(letter.status)}
                          <span style={{ marginLeft: '8px', fontSize: '14px', color: '#4b5563', textTransform: 'capitalize' }}>
                            {letter.status.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <button
                          onClick={() => navigate('/letters')}
                          style={{
                            color: '#2563eb',
                            fontSize: '14px',
                            fontWeight: '500',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Consent Modal */}
      {showConsentModal && selectedPatient && (
        <ConsentModal
          patientId={selectedPatient.id}
          patientName={selectedPatient.name}
          onClose={() => {
            setShowConsentModal(false);
            setSelectedPatient(null);
          }}
        />
      )}
    </div>
  );
}

export default DashboardPage;
